import React, { useEffect } from "react";
import {
  UseFormRegister,
  FieldErrors,
  Control,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { Plus, Trash2, Package } from "lucide-react";
import Button from "../UI/Button";
import { Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { PACKAGE_TYPES } from "../../constants/formOptions";
import { useTranslation } from "react-i18next";
import { translateValidation } from "../../utils/translateValidation";

interface CargoItemsSectionProps {
  register: UseFormRegister<CreateShipmentData>;
  errors: FieldErrors<CreateShipmentData>;
  readonly: boolean;
  getErrorMessage: (error: any) => string | undefined;
  fields: any[];
  append: (item: any) => void;
  remove: (index: number) => void;
  totalQuantity: number;
  totalWeight: number;
  totalVolume: number;
  totalLDM: number;
  control: Control<CreateShipmentData>;
  setValue: UseFormSetValue<CreateShipmentData>;
  transportMode?: "ROAD" | "SEA" | "AIR";
}

const CargoRowWatcher = ({
  index,
  control,
  setValue,
  transportMode = "ROAD",
}: {
  index: number;
  control: Control<CreateShipmentData>;
  setValue: UseFormSetValue<CreateShipmentData>;
  transportMode?: "ROAD" | "SEA" | "AIR";
}) => {
  const length = useWatch({ control, name: `cargoItems.${index}.length` });
  const width = useWatch({ control, name: `cargoItems.${index}.width` });
  const height = useWatch({ control, name: `cargoItems.${index}.height` });
  const weightKg = useWatch({ control, name: `cargoItems.${index}.weightKg` });
  const currentLdm = useWatch({ control, name: `cargoItems.${index}.ldm` });
  const currentVolume = useWatch({
    control,
    name: `cargoItems.${index}.volumeM3`,
  });

  useEffect(() => {
    const l = Number(length);
    const w = Number(width);
    const h = Number(height);

    // Auto-fill LDM and Volume from L/W/H ONLY when the field is empty/zero.
    // Once the user types a value, the auto-compute backs off so manual
    // entries survive subsequent dimension edits. To re-enable auto-fill,
    // the user clears the field back to 0 / empty.
    if (l > 0 && w > 0) {
      const ldmEmpty = !currentLdm || Number(currentLdm) === 0;
      if (ldmEmpty) {
        const ldm = Number(((l * w) / 10000 / 2.4).toFixed(3));
        setValue(`cargoItems.${index}.ldm`, ldm, { shouldValidate: false });
      }

      if (h > 0) {
        const volumeEmpty = !currentVolume || Number(currentVolume) === 0;
        if (volumeEmpty) {
          const volume = Number(((l * w * h) / 1000000).toFixed(3));
          setValue(`cargoItems.${index}.volumeM3`, volume, {
            shouldValidate: false,
          });
        }
      }
    }
    // Deliberately exclude currentLdm / currentVolume from the deps so this
    // doesn't loop when the auto-fill writes back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [length, width, height, index, setValue]);

  // AIR only: chargeableWeight = max(grossWeight, L x W x H / 6000).
  // Always recomputed (read-only in the UI) and persisted with the shipment.
  useEffect(() => {
    if (transportMode !== "AIR") return;

    const l = Number(length) || 0;
    const w = Number(width) || 0;
    const h = Number(height) || 0;
    const gross = Number(weightKg) || 0;

    const volumetric = l > 0 && w > 0 && h > 0 ? (l * w * h) / 6000 : 0;
    const chargeable = Number(Math.max(gross, volumetric).toFixed(2));

    setValue(`cargoItems.${index}.chargeableWeight`, chargeable, {
      shouldValidate: false,
    });
  }, [transportMode, length, width, height, weightKg, index, setValue]);

  return null;
};

export const CargoItemsSection: React.FC<CargoItemsSectionProps> = ({
  register,
  readonly,
  fields,
  append,
  remove,
  totalQuantity,
  totalWeight,
  errors,
  totalLDM,
  totalVolume,
  control,
  setValue,
  transportMode = "ROAD",
}) => {
  const { t } = useTranslation("shipments");

  // LDM is road-specific; chargeable weight is air-specific.
  const isRoad = transportMode === "ROAD";
  const isAir = transportMode === "AIR";

  const addCargoItem = () => {
    // Numeric fields start blank (undefined), not 0 — 0 is a real, distinct
    // value the user can still enter explicitly.
    append({
      quantity: undefined,
      type: "",
      weightKg: undefined,
      volumeM3: undefined,
      ldm: undefined,
      chargeableWeight: undefined,
      width: undefined,
      height: undefined,
      length: undefined,
      description: "",
      marks: "",
    });
  };

  const arrayError =
    errors?.cargoItems?.root?.message ||
    (typeof (errors?.cargoItems as any)?.message === "string"
      ? (errors?.cargoItems as any).message
      : undefined);

  const rowErrors = Array.isArray(errors?.cargoItems)
    ? (errors.cargoItems as Record<string, any>[])
    : [];

  const fieldHasError = (index: number, field: string): boolean => {
    const row = rowErrors[index];
    return !!(row && row[field]);
  };

  const th =
    "px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider";
  const td = "px-2 py-1.5 whitespace-nowrap align-top";

  return (
    <SectionCard
      icon={Package}
      title={t("cargo.title")}
      actions={
        !readonly && (
          <Button type="button" onClick={addCargoItem} variant="primary">
            <Plus className="w-3.5 h-3.5 mr-1" />
            {t("cargo.addItem")}
          </Button>
        )
      }
    >
      {arrayError && (
        <p className="text-[12px] text-red-600 mb-2">
          {translateValidation(arrayError)}
        </p>
      )}

      {fields.length > 0 && (
        <div className="border border-gray-200 rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 text-nowrap border-b border-gray-200">
                <tr>
                  <th className={th}>{t("cargo.qty")}</th>
                  <th className={th}>{t("cargo.type")}</th>
                  <th className={th}>{t("cargo.weightKg")}</th>
                  <th className={th}>{t("cargo.volumeM3")}</th>
                  <th className={th}>
                    {t("cargo.dimensions")}{" "}
                    <span className="font-normal text-gray-400 normal-case">
                      {t("cargo.lxwxh")}
                    </span>
                  </th>
                  {isRoad && <th className={th}>{t("cargo.ldm")}</th>}
                  {isAir && (
                    <th className={th}>{t("cargo.chargeableWeight")}</th>
                  )}
                  <th className={th}>{t("cargo.description")}</th>
                  <th className={th}>{t("cargo.marks")}</th>
                  {!readonly && <th className={th}>{t("cargo.action")}</th>}
                </tr>
              </thead>
              <tbody className="bg-white">
                {fields.map((field, index) => (
                  <tr
                    key={field.id || index}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <CargoRowWatcher
                      index={index}
                      control={control}
                      setValue={setValue}
                      transportMode={transportMode}
                    />
                    <td className={td}>
                      <Input
                        {...register(`cargoItems.${index}.quantity` as const)}
                        type="number"
                        min={0}
                        readOnly={readonly}
                        size="sm"
                        style={{ width: "72px" }}
                        error={fieldHasError(index, "quantity")}
                      />
                    </td>
                    <td className={td}>
                      <Select
                        {...register(`cargoItems.${index}.type` as const)}
                        options={PACKAGE_TYPES}
                        disabled={readonly}
                        size="sm"
                        placeholder={t("cargo.select")}
                        error={fieldHasError(index, "type")}
                      />
                    </td>
                    <td className={td}>
                      <Input
                        {...register(`cargoItems.${index}.weightKg` as const)}
                        type="number"
                        step="0.01"
                        min={0}
                        readOnly={readonly}
                        size="sm"
                        style={{ width: "90px" }}
                        error={fieldHasError(index, "weightKg")}
                      />
                    </td>
                    <td className={td}>
                      <Input
                        {...register(`cargoItems.${index}.volumeM3` as const)}
                        type="number"
                        step="0.01"
                        min={0}
                        readOnly={readonly}
                        size="sm"
                        style={{ width: "90px" }}
                        error={fieldHasError(index, "volumeM3")}
                      />
                    </td>
                    <td className={td}>
                      <div className="flex space-x-1">
                        <Input
                          {...register(`cargoItems.${index}.length` as const)}
                          type="number"
                          min={0}
                          readOnly={readonly}
                          size="sm"
                          style={{ width: "54px" }}
                          placeholder={t("cargo.length")}
                          error={fieldHasError(index, "length")}
                        />
                        <Input
                          {...register(`cargoItems.${index}.width` as const)}
                          type="number"
                          min={0}
                          readOnly={readonly}
                          size="sm"
                          style={{ width: "54px" }}
                          placeholder={t("cargo.width")}
                          error={fieldHasError(index, "width")}
                        />
                        <Input
                          {...register(`cargoItems.${index}.height` as const)}
                          type="number"
                          min={0}
                          readOnly={readonly}
                          size="sm"
                          style={{ width: "54px" }}
                          placeholder={t("cargo.height")}
                          error={fieldHasError(index, "height")}
                        />
                      </div>
                    </td>
                    {isRoad && (
                      <td className={td}>
                        <Input
                          {...register(`cargoItems.${index}.ldm` as const)}
                          type="number"
                          step="0.01"
                          min={0}
                          readOnly={readonly}
                          size="sm"
                          style={{ width: "72px" }}
                          error={fieldHasError(index, "ldm")}
                        />
                      </td>
                    )}
                    {isAir && (
                      <td className={td}>
                        <Input
                          {...register(
                            `cargoItems.${index}.chargeableWeight` as const,
                          )}
                          type="number"
                          step="0.01"
                          readOnly
                          size="sm"
                          style={{ width: "90px" }}
                          className="bg-gray-50"
                          error={fieldHasError(index, "chargeableWeight")}
                        />
                      </td>
                    )}
                    <td className={td}>
                      <Input
                        {...register(
                          `cargoItems.${index}.description` as const,
                        )}
                        readOnly={readonly}
                        size="sm"
                        placeholder={t("cargo.description")}
                        style={{ width: "150px" }}
                        error={fieldHasError(index, "description")}
                      />
                    </td>
                    <td className={td}>
                      <Input
                        {...register(`cargoItems.${index}.marks` as const)}
                        readOnly={readonly}
                        size="sm"
                        placeholder={t("cargo.remarks")}
                        style={{ width: "150px" }}
                      />
                    </td>
                    {!readonly && (
                      <td className={td}>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {fields.length > 0 && (
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-[12px]">
              <span className="font-semibold text-gray-600 uppercase tracking-wide">
                {t("cargo.totals")}
              </span>
              <div className="flex gap-6">
                <div>
                  <span className="text-gray-500">{t("cargo.quantity")}</span>{" "}
                  <span className="font-semibold text-gray-900">
                    {totalQuantity}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">{t("cargo.weight")}</span>{" "}
                  <span className="font-semibold text-gray-900">
                    {totalWeight.toFixed(2)} kg
                  </span>
                </div>
                {isRoad && (
                  <div>
                    <span className="text-gray-500">
                      {t("cargo.ldmTotal")}
                    </span>{" "}
                    <span className="font-semibold text-gray-900">
                      {totalLDM.toFixed(3)}
                    </span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">{t("cargo.volTotal")}</span>{" "}
                  <span className="font-semibold text-gray-900">
                    {totalVolume.toFixed(3)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
};
